export const loadRazorpayScript = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const existing = document.getElementById("razorpay-sdk");
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => reject(paymentError("script", "Failed to load Razorpay")));
      return;
    }

    const script = document.createElement("script");
    script.id = "razorpay-sdk";
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => {
      // Drop the failed tag so the next attempt retries the download instead
      // of waiting on a load event that already fired.
      script.remove();
      reject(paymentError("script", "Failed to load Razorpay"));
    };
    document.body.appendChild(script);
  });

export const createRazorpayOrder = async ({ backendUrl, amount, currency = "INR", receipt, notes }) => {
  const token = localStorage.getItem("token");
  console.log("Creating Razorpay order:", { backendUrl, amount, hasToken: !!token });
  
  const response = await fetch(`${backendUrl}/api/payments/razorpay/order`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token && { "Authorization": `Bearer ${token}` }),
    },
    body: JSON.stringify({ amount, currency, receipt, notes }),
  });

  console.log("Razorpay order response status:", response.status);
  
  if (!response.ok) {
    const message = await response.text();
    console.error("Razorpay order error:", message);
    let detail = message;
    try { detail = JSON.parse(message)?.detail || message; } catch { /* plain text */ }
    throw paymentError("order", detail || "Unable to create payment order", response.status);
  }

  return response.json();
};

// Errors from the steps before the Razorpay window opens carry a `stage`
// ("script" | "order") so the page can say what went wrong instead of a
// blanket "Checkout failed".
const paymentError = (stage, message, status) =>
  Object.assign(new Error(message), { stage, status });

// User-facing text for a failed checkout start. `detail` is the technical
// reason, shown small so a tester can report it.
export const describePaymentError = (error) => {
  if (error?.stage === "script") {
    return {
      message: "Couldn't open the payment window. Check your internet connection and try again.",
      detail: error.message,
    };
  }
  if (error?.stage === "order") {
    return {
      message: error.status >= 500
        ? "Payments are unavailable right now. Please try again in a little while."
        : "We couldn't start the payment. Please try again.",
      detail: `${error.status || ""} ${error.message}`.trim(),
    };
  }
  return {
    message: "Checkout failed. Please try again.",
    detail: error?.message || "",
  };
};

export const verifyRazorpayPayment = async ({ backendUrl, payload }) => {
  const token = localStorage.getItem("token");
  const response = await fetch(`${backendUrl}/api/payments/razorpay/verify`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token && { "Authorization": `Bearer ${token}` }),
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || "Payment verification failed");
  }

  return response.json();
};

// ── UPI Collect stuck-checkout rescue ──────────────────────────────────────
// Razorpay's `handler` callback only fires when their iframe successfully
// polls their own servers for payment status. UPI Collect payments are
// server-polled, and if the poll drops (iframe throttled, session expired
// after ~5 minutes, mobile network hiccup) the modal freezes on
// "waiting for payment" even after the customer's account has been
// debited. This helper polls our backend, which in turn queries Razorpay's
// server-to-server API — bypassing the fragile iframe poll entirely.
//
// Returns a `stop()` function; callers must invoke it in `handler`,
// `ondismiss`, and `payment.failed` to prevent leaked timers.
export const pollRazorpayOrderStatus = ({
  backendUrl,
  orderId,
  intervalMs = 4000,
  timeoutMs = 5 * 60 * 1000,  // stop polling after 5 min
  onPaid,     // (info) => void, called ONCE when order flips to paid
}) => {
  const token = localStorage.getItem("token");
  let stopped = false;
  const startedAt = Date.now();

  const stop = () => { stopped = true; };

  const tick = async () => {
    if (stopped) return;
    if (Date.now() - startedAt > timeoutMs) { stop(); return; }
    try {
      const res = await fetch(
        `${backendUrl}/api/payments/razorpay/order/${orderId}/status`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (res.ok) {
        const body = await res.json();
        if (body.status === "paid" && body.captured_payment_id) {
          stop();
          onPaid(body);
          return;
        }
      }
    } catch { /* transient network — try again */ }
    if (!stopped) setTimeout(tick, intervalMs);
  };
  setTimeout(tick, intervalMs);
  return stop;
};

// ── Prefill helpers ────────────────────────────────────────────────────────
// Razorpay's UPI Intent flow (mandatory post-Feb-2026 NPCI guidelines) only
// renders UPI as a checkout option when `prefill.contact` is a valid Indian
// E.164 number. Missing `contact` = UPI hidden. This helper normalises the
// stored mobile_number ("9876543210" / "+919876543210" / "919876543210") to
// the canonical "+91XXXXXXXXXX" form expected by Razorpay.
export const normalizeIndianMobile = (raw) => {
  if (!raw) return "";
  const digits = String(raw).replace(/\D/g, "");
  if (digits.length === 10) return `+91${digits}`;
  if (digits.length === 12 && digits.startsWith("91")) return `+${digits}`;
  if (digits.length === 11 && digits.startsWith("0")) return `+91${digits.slice(1)}`;
  if (digits.length === 13 && digits.startsWith("091")) return `+${digits.slice(1)}`;
  // Not a recognisable Indian number — return empty so we don't break Razorpay
  // with an invalid contact. UPI may still hide, but checkout will load.
  return "";
};

// Build a Razorpay `prefill` object from the locally-cached user. Includes
// `contact` only when we have a valid Indian mobile (otherwise UPI Intent
// silently disappears from the checkout modal).
export const buildRazorpayPrefill = (overrides = {}) => {
  let user = null;
  try {
    const raw = localStorage.getItem("user");
    if (raw) user = JSON.parse(raw);
  } catch {
    user = null;
  }
  const name = overrides.name ?? user?.name ?? "";
  const email = overrides.email ?? user?.email ?? "";
  const rawContact = overrides.contact ?? user?.mobile_number ?? "";
  const contact = normalizeIndianMobile(rawContact);
  const prefill = {};
  if (name)    prefill.name = name;
  if (email)   prefill.email = email;
  if (contact) prefill.contact = contact;
  return prefill;
};

// Apply our merchant's saved Payment Configuration (created in the Razorpay
// Dashboard) to a checkout `options` object. The Configuration ID controls
// which payment methods + UPI sub-options (QR / Apps / ID-Number) display
// and in what order. Reads from the build-time env var
// REACT_APP_RAZORPAY_CHECKOUT_CONFIG_ID — when unset, Razorpay falls back to
// the account default (which may hide UPI ID/Number).
//
// Reference:
//   https://razorpay.com/docs/payments/payment-gateway/web-integration/standard/configure-payment-methods/display-configuration/
export const withRazorpayCheckoutConfig = (options) => {
  // Temporary escape hatch — set REACT_APP_RAZORPAY_DISABLE_CONFIG=true
  // to fall back to Razorpay's account-default checkout UI and rule out
  // a stale/misassigned checkout_config_id as the cause of Razorpay's
  // "Uh oh! Something went wrong" modal.
  if (process.env.REACT_APP_RAZORPAY_DISABLE_CONFIG === 'true') return options;
  const configId = process.env.REACT_APP_RAZORPAY_CHECKOUT_CONFIG_ID;
  if (!configId) return options;

  // Auto-skip in live mode. Razorpay Payment Configuration IDs are bound to
  // the mode (test / live) they were created under. Sending a TEST-mode
  // config_id with a LIVE key_id makes Razorpay's checkout SDK reject the
  // /v2/standard_checkout/preferences call with 400 Bad Request. Until a
  // live-mode config ID is issued in the Razorpay Dashboard, drop the
  // param on live so checkout falls back to the account-default UI.
  const keyId = options.key || process.env.REACT_APP_RAZORPAY_KEY_ID || '';
  if (keyId.startsWith('rzp_live_')) return options;

  return { ...options, checkout_config_id: configId };
};

// Razorpay test-mode UPI testing notes:
//
// In test mode, the QR Razorpay shows is a MOCK — real UPI apps cannot scan it
// (test mode never moves real money on NPCI rails). To exercise the test-mode
// UPI flow, use the manual VPA field with:
//   success@razorpay  → mock success
//   failure@razorpay  → mock failure
//
// If the manual VPA ("Pay via UPI ID") field is missing from your checkout,
// it must be enabled at the merchant account level:
//   Razorpay Dashboard → Account & Settings → Payment Configuration →
//     Checkout Settings → Standard Payment Options → UPI block →
//     toggle ON "Show UPI ID/Number"
//
// Note: Per NPCI rules effective Feb 28, 2026, UPI Collect is suppressed on
// Android mobile by Razorpay regardless of dashboard settings. Test the
// manual VPA flow on a desktop browser.
//
// We intentionally do NOT pass a custom `config.display.blocks` object —
// custom blocks override Razorpay's smart device-aware rendering and can
// collapse the UPI section to a QR-only view that breaks the Continue
// button. Letting Razorpay use defaults keeps Intent + QR + Collect (where
// allowed) all visible and behaving correctly.
